import classNames from "classnames";
import { ICON } from "constants/icons";
import { RoutePath } from "data/router/paths";
import useCustomNavigate from "hooks/common/useCustomNavigate";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import styles from "./Header.module.scss";
import { HeaderProps } from "./Header.type";
import Popup from "components/Popup/Popup";
import Alert from "components/Popup/Alert/Alert";
import { authStore } from "stores/auth-stores";
import { WEB_STORAGE_KEYS } from "constants/webStorageKey";
import Button from "components/Button/Button";
import { BUTTON_COLOR } from "components/Button/Button.type";
import { makeQrWithSafeLogo } from "utils/qr-utils";

export default function Header({ menusVisible = true, downloadLink, fileName }: HeaderProps) {
  const location = useLocation();
  const { navigateToReplacePath } = useCustomNavigate();
  const [selectedMenu, setSelectedMenu] = useState("");
  const [isShowAlert, setIsShowAlert] = useState(false);
  const [qrSrc, setQrSrc] = useState<string>("");
  const { setLoginStatus } = authStore();

  // 유틸: 파일명 도출 + 확장자 교체(옵션)
  const pickFileName = (url: string, fallback = "download") => {
    try {
      const last = decodeURIComponent(new URL(url).pathname.split("/").pop() || "");
      return last || fallback;
    } catch {
      return fallback;
    }
  };

  const handleClickDownload = async (link?: string, preferredName?: string) => {
    if (!link) return;

    // 파일명: prop > URL에서 추출
    const nameFromUrl = pickFileName(link);
    const fileName = preferredName || nameFromUrl;

    // 1) Blob으로 받아 저장 (CORS 허용 필요)
    try {
      const res = await fetch(link, { mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();

      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = fileName; // 여기서 파일명 강제 가능
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      a.remove();
      // 메모리 해제
      setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
      return;
    } catch (err) {
      // 2) 폴백: 새 탭/창으로 열기 (서버가 Content-Disposition 설정돼 있다면 브라우저가 다운로드로 처리)
      console.warn("Blob download failed, fallback to window.open:", err);
      window.open(link, "_blank", "noopener");
    }
  };

  const handleGenerate = async () => {
    console.log("download link", downloadLink);
    if (!downloadLink) return;
    const dataUrl = await makeQrWithSafeLogo({
      text: downloadLink,
      logoUrl: ICON.ICON_DUCK, // 같은 도메인 이미지를 권장(또는 CORS 허용 CDN)
      size: 512,
      logoRatio: 0.26, // 중앙 빈 영역(로고 박스) 크기
      margin: 20,
      dotRadius: 0.48, // 둥근 점 느낌
    });
    setQrSrc(dataUrl);
  };

  const isRiple = useMemo(() => {
    return location.pathname === RoutePath.ripeness;
  }, [location]);

  const handleLogout = () => {
    // 세션 스토리지 초기화
    setIsShowAlert(false);
    sessionStorage.removeItem(WEB_STORAGE_KEYS.EE_LOGIN_USER_NAME);
    sessionStorage.removeItem(WEB_STORAGE_KEYS.LOGIN_STATUS);
    setLoginStatus(false);
    navigateToReplacePath(RoutePath.login);
  };

  const QrPopup = useMemo(() => {
    return (
      <div className={styles["qr-popup__container"]}>
        <div className={styles["qr-popup__img"]}>
          <img src={qrSrc} alt="QR Code" style={{ width: "300px", height: "300px" }}></img>
          <span>{fileName}</span>
        </div>
      </div>
    );
  }, [qrSrc, fileName]);

  const rightButtons = useMemo(() => {
    return !menusVisible ? (
      <>
        <button
          className={styles["download__wrap"]}
          onClick={() => {
            handleClickDownload(downloadLink);
          }}
        >
          <div className={classNames(styles["-menu"])}>APP Download</div>
        </button>
        <button
          className={styles["download__wrap"]}
          onClick={() => {
            handleGenerate();
            setIsShowAlert(true);
          }}
        >
          <div className={classNames(styles["-menu"])}>QR</div>
        </button>
      </>
    ) : (
      <>
        <button
          className={styles["download__wrap"]}
          onClick={() => {
            navigateToReplacePath(isRiple ? RoutePath.dashborad : RoutePath.ripeness);
          }}
        >
          <div className={classNames(styles["-menu"])}> {isRiple ? "Statistik" : "Home"}</div>
        </button>
        <button
          className={styles["download__wrap"]}
          onClick={() => {
            setIsShowAlert(true);
          }}
        >
          <div className={classNames(styles["-menu"])}>Logout</div>
        </button>
      </>
    );
  }, [menusVisible, downloadLink, isRiple]);

  const showAlert = useMemo(() => {
    return menusVisible ? <Alert contents={<span>Do you want to leave?</span>} onClosePopup={() => setIsShowAlert(false)} firstButton={<Button color={BUTTON_COLOR.white} text="Cancel" onClick={() => setIsShowAlert(false)} width={120} />} secondButton={<Button color={BUTTON_COLOR.black} text="Confirm" onClick={() => handleLogout()} />} /> : <Alert contents={QrPopup} onClosePopup={() => setIsShowAlert(false)}></Alert>;
  }, [menusVisible, qrSrc]); // alert에도 qrSrc가 변경되었음을 알아야 함

  return (
    <div className={styles["header__container"]}>
      <img className={styles["-logo"]} src={ICON.LOGO_DABEEO} alt="" onClick={() => navigateToReplacePath(RoutePath.home)} />
      <div className={styles["download__container"]}>{rightButtons}</div>

      {isShowAlert && showAlert}

      {/* {menusVisible && (
        <div className={styles["menu__wrap"]}>
          {MENUS.map(menu => {
            return <div key={menu.path} className={classNames(styles["-menu"], { [styles["-clicked"]]: menu.children.includes(selectedMenu) })} onClick={() => handleClickMenu(menu.path)}>{menu.name}</div>
          })}
        </div>
      )} */}
    </div>
  );
}
